import { useCallback, useContext, useMemo } from 'react';

import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import { ExtranetExperienceStatus } from 'app/typings/core/extranet/hotel/experiences';
import { ExtranetPackStatus } from 'app/typings/core/extranet/hotel/packs';

export const useFilteredExperiences = () => {
  const { experiencesList, packsList } = useContext(ModernHotelContext);
  const { data: experiencesCategories, isFetching } =
    useExperiencesCategories();

  const publishedPackExperienceIds = useMemo(() => {
    return new Set(
      packsList
        .filter((pack) =>
          [
            ExtranetPackStatus.PUBLISHED,
            ExtranetPackStatus.UNPUBLISHED,
          ].includes(pack.status)
        )
        .flatMap((pack) =>
          pack.packExperiences.map((packExp) => packExp.experienceId)
        )
    );
  }, [packsList]);

  const categoriesMap = useMemo(() => {
    return new Map(
      experiencesCategories?.map((cat) => [cat.id, cat.name]) || []
    );
  }, [experiencesCategories]);

  const isInfiniteExperience = useCallback(
    (expId: number) => {
      const experience = experiencesList.find((exp) => exp.id === expId);
      const stockConfiguration =
        experience?.openingConfiguration?.stockConfiguration;

      const isConstantWithNullStock =
        stockConfiguration?.type === OPENING_CONFIGURATION_TYPE.CONSTANT &&
        (stockConfiguration.configuration?.stock === null ||
          stockConfiguration.configuration?.stock === undefined);

      const isDailyWithAllDaysUnlimited =
        stockConfiguration?.type === OPENING_CONFIGURATION_TYPE.DAILY &&
        Object.values(stockConfiguration.days).every(
          (dayConfig) =>
            !dayConfig.isClosed &&
            (dayConfig.stock === null || dayConfig.stock === undefined)
        );

      return (
        stockConfiguration !== undefined &&
        (isConstantWithNullStock || isDailyWithAllDaysUnlimited)
      );
    },
    [experiencesList]
  );

  const sortedExperiences = useMemo(() => {
    const sorted = experiencesList
      .map((exp) => ({
        experience: exp,
        isPublished: exp.status === ExtranetExperienceStatus.PUBLISHED,
        isInOnlinePack: publishedPackExperienceIds.has(exp.id),
        categoryName: categoriesMap.get(exp.categoryId) || '',
        isAlwaysIncludedWithNight: exp.isAlwaysIncludedWithNight,
      }))
      .filter(
        (item) =>
          item.isPublished &&
          (item.isInOnlinePack || item.isAlwaysIncludedWithNight) &&
          item.experience.showInInventory
      )
      .sort((a, b) => {
        return a.categoryName.localeCompare(b.categoryName, 'fr', {
          sensitivity: 'base',
        });
      });

    const sortedExps = sorted.reduce(
      (acc, item) => {
        if (item.isAlwaysIncludedWithNight) {
          acc.alwaysIncluded.push(item.experience);
        } else {
          acc.regular.push(item.experience);
        }

        return acc;
      },
      {
        regular: [] as typeof experiencesList,
        alwaysIncluded: [] as typeof experiencesList,
      }
    );

    return {
      ...sortedExps,
      alwaysIncluded: sortedExps.alwaysIncluded.sort(
        (a, b) => (a.hotelPriority ?? Infinity) - (b.hotelPriority ?? Infinity)
      ),
    };
  }, [experiencesList, publishedPackExperienceIds, categoriesMap]);

  return {
    sortedExperiences: sortedExperiences.regular,
    sortedExperiencesIsAlwaysIncludedWithNight:
      sortedExperiences.alwaysIncluded,
    experiencesCategories,
    isInfiniteExperience,
    isFetching,
  };
};
